import React, { useState, useEffect } from "react";
import { auth } from "../../firebase";
import { parseHTMLContent } from "../documentation/htmlParsingUtils";
import { Quill } from "react-quill-new";
import "../../utils/quillFonts";
import ImageResize from "quill-image-resize-module-react";
import QuillTableBetter from "quill-table-better";

// CSS imports for proper styling (same as regular ConfigureSpaceImport)
import "quill-better-table/dist/quill-better-table.css";
import "react-quill-new/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";
import "../../css/ImportDocument.css";
import "../../css/Create.css";
import "../../css/FinalizeImportedWorkspaceDocument.css"
import "../../css/QuillOverrides.css";


// Register Quill modules for proper processing
Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);

// Quill Core Imports for custom blots
const Inline = Quill.import("blots/inline");
const Block = Quill.import("blots/block");

// Custom Inline Code Block (same as regular ConfigureSpaceImport)
class InlineCode extends Inline {
    static create() {
        const node = super.create();
        node.setAttribute("class", "inline-code");
        return node;
    }
    static formats(node) {
        return true;
    }
}
InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";

// Custom Multi-line Blockquote (same as regular ConfigureSpaceImport)
class MultiLineBlockquote extends Block {
    static create() {
        const node = super.create();
        node.setAttribute("class", "multi-line-blockquote");
        return node;
    }
    static formats(node) {
        return true;
    }
}
MultiLineBlockquote.blotName = "multi-line-blockquote";
MultiLineBlockquote.tagName = "multi-line-blockquote";
MultiLineBlockquote.className = "multi-line-blockquote";

// Register Custom Quill Components
Quill.register(MultiLineBlockquote);
Quill.register(InlineCode);

function ConfigureWorkspaceSpaceImport({ extractedData, onNext, onBack, workspaceDetails }) {
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [uid, setUid] = useState("");
    const [importProgress, setImportProgress] = useState(null);
    const [spaceConfig, setSpaceConfig] = useState({
        spaceName: extractedData?.suggestedSpaceName || "Imported Space",
        spaceDescription: "",
        makePublic: false,
        selectedFiles: extractedData?.files?.reduce((acc, file) => {
            acc[file.path] = true; // Select all files by default
            return acc;
        }, {}) || {},
        processImages: true,
        preserveHierarchy: true
    });

    // Authentication Effect
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
            } else {
                setUid("");
            }
        });

        return () => {
            unsubscribe();
        };
    }, []);

    const handleInputChange = (field, value) => {
        setSpaceConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFileSelection = (filePath, selected) => {
        setSpaceConfig(prev => ({
            ...prev,
            selectedFiles: {
                ...prev.selectedFiles,
                [filePath]: selected
            }
        }));
    };

    const handleSelectAll = () => {
        const allSelected = Object.values(spaceConfig.selectedFiles).every(Boolean);
        const newSelection = {};
        extractedData.files.forEach(file => {
            newSelection[file.path] = !allSelected;
        });
        setSpaceConfig(prev => ({
            ...prev,
            selectedFiles: newSelection
        }));
    };

    const identifySpaceContent = (files) => {
        // Look for the main space file - typically the one that matches the space name
        // or has patterns like "TimedInfo", "index", or is the largest content file
        const spaceFile = files.find(file => {
            const fileName = file.name.toLowerCase();
            return fileName.includes('timedinfo') || 
                   fileName.includes('info') ||
                   fileName === 'index.html' ||
                   // Fallback: largest file that's not obviously a page
                   (file.content.length > 1000 && !fileName.includes('database') && !fileName.includes('general'));
        });
        
        return spaceFile || files[0]; // Fallback to first file
    };

    const classifyFiles = (files) => {
        const spaceContent = identifySpaceContent(files);
        const pages = files.filter(file => 
            file !== spaceContent && 
            file.name.endsWith('.html') && 
            file.name !== 'index.html'
        );
        const assets = files.filter(file => 
            !file.name.endsWith('.html') || 
            file.name === 'index.html'
        );

        return { spaceContent, pages, assets };
    };

    const createWorkspaceSpaceAndPages = async (config) => {
        const allFiles = extractedData.files; // Get all files for image processing
        const { spaceContent, pages, assets } = classifyFiles(
            allFiles.filter(file => config.selectedFiles[file.path])
        );

        const totalSteps = 1 + pages.length; // 1 space + N pages
        let currentStep = 0;

        try {
            // Step 1: Create the Workspace Space
            setImportProgress({ step: 'Creating workspace space', current: ++currentStep, total: totalSteps });
            const createdSpace = await createWorkspaceSpace({
                name: config.spaceName,
                description: config.spaceDescription,
                content: await processHtmlContent(spaceContent.content, spaceContent.name, allFiles),
                isPublic: config.makePublic
            });

            // Validate that we have a space ID
            if (!createdSpace || !createdSpace.id) {
                throw new Error('Failed to create workspace space or get space ID');
            }

            // Step 2: Create Pages sequentially in the workspace
            const createdPages = [];
            for (const page of pages) {
                setImportProgress({ 
                    step: `Processing page: ${cleanFileName(page.name)}`, 
                    current: ++currentStep, 
                    total: totalSteps 
                });
                const pageId = await createWorkspacePage({
                    name: cleanFileName(page.name),
                    content: await processHtmlContent(page.content, page.name, allFiles),
                    spaceId: createdSpace.id, // Use the actual space ID
                    parentName: config.spaceName, // For now, all pages are direct children of space
                    isPublic: config.makePublic
                });
                createdPages.push({ ...page, id: pageId });
            }

            setImportProgress({ step: 'Import completed!', current: totalSteps, total: totalSteps });

            return {
                spaceCreated: true, // Just indicate that space was created
                spaceId: createdSpace.id, // Include the space ID
                pages: createdPages,
                assets
            };

        } catch (error) {
            setImportProgress(null);
            throw error;
        }
    };

    const cleanFileName = (fileName) => {
        // Convert filename to a clean page name
        return fileName
            .replace('.html', '')
            .replace(/[_\-]/g, ' ')
            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .trim()
            .replace(/^\w/, c => c.toUpperCase()); // Capitalize first letter
    };

    const extractPageIdFromFilename = (filename) => {
        // Extract page ID from filename like "Database-Tables_2628255748.html" or "2628255748.html"
        let match = filename.match(/_(\d+)\.html$/);
        if (!match) {
            match = filename.match(/(\d+)\.html$/);
        }
        return match ? match[1] : null;
    };

    const convertHtmlToQuillDelta = async (htmlContent) => {
        // Create a temporary container (same as regular version)
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.style.width = '800px'; // Set a reasonable width
        document.body.appendChild(tempContainer);

        try {
            // Create a temporary Quill instance with the same modules
            const tempQuill = new Quill(tempContainer, {
                theme: 'snow',
                modules: {
                    toolbar: false,
                    history: {
                        delay: 1000,
                        maxStack: 100,
                        userOnly: true
                    },
                    imageResize: {
                        parchment: Quill.import("parchment"),
                        modules: ["Resize", "DisplaySize"],
                    },
                    table: false,
                    "table-better": {
                        language: "en_US",
                        menus: [
                            "column",
                            "row", 
                            "merge",
                            "table",
                            "cell",
                            "wrap",
                            "copy",
                            "delete",
                        ],
                        toolbarTable: true,
                        operationMenu: {
                            items: {
                                insertColumnLeft: { text: 'Insert column left' },
                                insertColumnRight: { text: 'Insert column right' },
                                insertRowUp: { text: 'Insert row above' },
                                insertRowDown: { text: 'Insert row below' },
                                mergeCells: { text: 'Merge cells' },
                                unmergeCells: { text: 'Unmerge cells' },
                                deleteColumn: { text: 'Delete column' },
                                deleteRow: { text: 'Delete row' },
                                deleteTable: { text: 'Delete table' }
                            }
                        }
                    }
                }
            });

            // Use Quill's clipboard to convert HTML to Delta and then back to HTML
            tempQuill.clipboard.dangerouslyPasteHTML(0, htmlContent);
            
            // Add a small delay to ensure proper processing
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Get the Delta representation first, then convert back to HTML
            const delta = tempQuill.getContents();
            
            // Clear and set content using Delta to ensure proper processing
            tempQuill.setContents(delta);
            
            // Add another small delay
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // Now get the final HTML after full Quill processing
            const processedHtml = tempQuill.root.innerHTML;
            
            return processedHtml;
        } catch (error) {
            // Fallback to original content if Quill processing fails
            return htmlContent;
        } finally {
            // Cleanup
            if (tempContainer.parentNode) {
                document.body.removeChild(tempContainer);
            }
        }
    };

    const processHtmlContent = async (htmlContent, filename = '', allFiles = []) => {
        if (!htmlContent) return '';

        try {
            // Get matching images for this specific file
            let pageImages = [];
            if (filename && allFiles.length > 0) {
                const pageId = extractPageIdFromFilename(filename);
                if (pageId) {
                    // Find images for this page in attachments/<pageId>/ directory
                    pageImages = extractedData.images.filter(image => {
                        const imagePath = image.path.toLowerCase();
                        return imagePath.includes(`attachments/${pageId}/`);
                    });
                }
            }
            
            // Use the shared HTML parsing utility
            const parsedContent = await parseHTMLContent(htmlContent, pageImages);
            
            // Convert the parsed HTML to proper Quill format
            const quillContent = await convertHtmlToQuillDelta(parsedContent);
            
            return quillContent;
            
        } catch (error) {
            // Fallback to basic processing
            let processed = htmlContent;
            
            // Basic cleanup as fallback
            processed = processed.replace(/<div class="confluence-information-macro[^>]*>[\s\S]*?<\/div>/gi, '');
            processed = processed.replace(/<span class="confluence-anchor-link[^>]*>[\s\S]*?<\/span>/gi, '');
            processed = processed.replace(/<nav[^>]*>[\s\S]*?<\/nav>/gi, '');
            processed = processed.replace(/<div class=".*?navigation.*?">[\s\S]*?<\/div>/gi, '');
            
            // Remove common empty elements that cause spacing
            processed = processed.replace(/<p>\s*<\/p>/gi, '');
            processed = processed.replace(/<div>\s*<\/div>/gi, '');
            processed = processed.replace(/^\s*(<br\s*\/?>)+/gi, '');
            
            // Clean up relative links
            processed = processed.replace(/href="(?!http)([^"]+)"/gi, 'href="#$1"');
            processed = processed.replace(/src="(?!http)([^"]+)"/gi, 'src="#$1"');
            
            // Trim leading whitespace
            processed = processed.trim();
            
            return processed;
        }
    };

    const createWorkspaceSpace = async (spaceData) => {
        const user = auth.currentUser;
        const token = user ? await user.getIdToken() : null;

        if (!token) {
            throw new Error("User is not authenticated.");
        }

        const data = {
            name: spaceData.name,
            description: spaceData.description,
            content: spaceData.content,
            is_public: spaceData.isPublic,
            completed: true, // Auto-publish imported content
        };

        const response = await fetch(
            `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceDetails.id}/spaces/create/`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                    "Requesting-User-Uid": uid,
                },
                body: JSON.stringify(data),
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to create workspace space.");
        }

        const result = await response.json();
        
        // Validate that we have the required space ID
        if (!result || (!result.id && !result.spaceId && !result.space_id)) {
            throw new Error("Failed to get space ID from API response.");
        }

        // Return the space object with the ID
        const spaceId = result.id || result.spaceId || result.space_id;
        
        return {
            id: spaceId,
            name: spaceData.name,
            description: spaceData.description
        };
    };

    const createWorkspacePage = async (pageData) => {
        const user = auth.currentUser;
        const token = user ? await user.getIdToken() : null;

        if (!token) {
            throw new Error("User is not authenticated.");
        }

        // Validate that we have the required space ID
        if (!pageData.spaceId) {
            throw new Error('Space ID is required to create a workspace page.');
        }

        const data = {
            page_name: pageData.name,
            description: '', // Could extract from content later
            content: pageData.content,
            is_public: pageData.isPublic,
            completed: true, // Auto-publish imported content
            space_id: pageData.spaceId, // Link page to the created space
        };

        const response = await fetch(
            `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceDetails.id}/pages/create/`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                    "Requesting-User-Uid": uid,
                },
                body: JSON.stringify(data),
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to create workspace page.");
        }

        const result = await response.json();
        return result.id || result.pageId; // Return the created page ID
    };

    const handleNext = async () => {
        if (canProceed()) {
            setIsTransitioning(true);
            
            try {
                // Start the import process
                const result = await createWorkspaceSpaceAndPages(spaceConfig);
                
                // Wait a moment to show completion, then proceed
                setTimeout(() => {
                    setImportProgress(null);
                    onNext({
                        ...spaceConfig,
                        authorUid: uid, // Add the user UID to the config
                        importResult: result
                    });
                }, 1500);
            } catch (error) {
                alert(`Import failed: ${error.message}`);
                setImportProgress(null);
                setIsTransitioning(false);
            }
        }
    };

    const handleBack = () => {
        setIsTransitioning(true);
        setTimeout(() => {
            onBack();
        }, 150);
    };

    const canProceed = () => {
        return spaceConfig.spaceName.trim() && 
               Object.values(spaceConfig.selectedFiles).some(Boolean);
    };

    const getSelectedFileCount = () => {
        return Object.values(spaceConfig.selectedFiles).filter(Boolean).length;
    };

    const getTotalFileCount = () => {
        return extractedData?.files?.length || 0;
    };

    return (
        <>
            {/* Configuration Form - Hidden when showing progress */}
            {!importProgress && (
                <div className={`ImportDocument__main-div ${isTransitioning ? 'transitioning' : ''}`}>
                    <div className="ImportDocument__content">
                        <h2>Configure Workspace Space Import</h2>
                        <p className="medium" style={{ marginTop: "20px" }}>
                            Configure your workspace space and select which files to import
                        </p>
                        
                        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                            <p><strong>Workspace:</strong> {workspaceDetails?.name || 'Loading...'}</p>
                        </div>

                        {/* Space Name */}
                        <div className="Create__form-group">
                            <label>Space Name: </label>
                            <input
                                type="text"
                                id="spaceName"
                                className="Create__input-short-field"
                                value={spaceConfig.spaceName}
                                onChange={(e) => handleInputChange('spaceName', e.target.value)}
                                placeholder="Enter space name"
                                maxLength={100}
                                required
                            />
                        </div>

                        {/* Space Description */}
                        <div className="Create__form-group">
                            <label>Space Description: </label>
                            <input
                                type="text"
                                id="spaceDescription"
                                className="Create__input-full-field"
                                value={spaceConfig.spaceDescription}
                                onChange={(e) => handleInputChange('spaceDescription', e.target.value)}
                                placeholder="Enter a brief description of this space"
                            />
                        </div>
                        <div className="Create__form-group-helper-text">
                            <i
                                className="fas fa-info-circle Create__info-icon"
                                title="Information"
                            ></i>
                            <p className="Create__helper-text">
                                This description is what team members will see about this space.
                            </p>
                        </div>

                        {/* File Selection Section */}
                        <div style={{ marginTop: "40px", marginBottom: "60px" }}>
                            <div style={{ 
                                display: "flex", 
                                justifyContent: "space-between", 
                                alignItems: "center",
                                marginBottom: "20px"
                            }}>
                                <h3 style={{ margin: "0", fontSize: "18px", fontWeight: "600" }}>
                                    Select Files to Import
                                </h3>
                                <div style={{ 
                                    display: "flex", 
                                    alignItems: "center", 
                                    gap: "15px" 
                                }}>
                                    <span style={{ 
                                        fontSize: "14px", 
                                        color: "var(--bb-text)",
                                        opacity: "0.8"
                                    }}>
                                        {getSelectedFileCount()} of {getTotalFileCount()} files selected
                                    </span>
                                    <button 
                                        type="button"
                                        onClick={handleSelectAll}
                                        className="ImportDocument__select-all-button"
                                    >
                                        {Object.values(spaceConfig.selectedFiles).every(Boolean) ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                            </div>

                            <div style={{ 
                                maxHeight: "300px", 
                                overflowY: "auto",
                                border: "1px solid rgba(255, 255, 255, 0.2)",
                                borderRadius: "8px",
                                backgroundColor: "white"
                            }}>
                                {extractedData?.files?.map((file, index) => (
                                    <div 
                                        key={file.path} 
                                        style={{ 
                                            padding: "12px 16px",
                                            borderBottom: index < extractedData.files.length - 1 ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                                            transition: "background-color 0.2s ease",
                                            backgroundColor: "white"
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)"}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                    >
                                        <label style={{ 
                                            display: "flex", 
                                            alignItems: "center", 
                                            cursor: "pointer",
                                            margin: "0"
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={spaceConfig.selectedFiles[file.path] || false}
                                                onChange={(e) => handleFileSelection(file.path, e.target.checked)}
                                                style={{ 
                                                    marginRight: "12px",
                                                    transform: "scale(1.2)"
                                                }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ 
                                                    fontWeight: "500", 
                                                    fontSize: "14px",
                                                    color: "var(--bb-text)",
                                                    marginBottom: "2px"
                                                }}>
                                                    {file.name}
                                                </div>
                                                <div style={{ 
                                                    fontSize: "12px", 
                                                    color: "var(--bb-text)",
                                                    opacity: "0.7",
                                                    marginBottom: "2px"
                                                }}>
                                                    {file.path}
                                                </div>
                                                <div style={{ 
                                                    fontSize: "11px", 
                                                    color: "var(--bb-text)",
                                                    opacity: "0.6"
                                                }}>
                                                    {Math.round(file.content.length / 1024)} KB
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {!canProceed() && (
                            <div style={{ 
                                marginTop: "20px", 
                                padding: "15px", 
                                backgroundColor: "rgba(244, 67, 54, 0.1)", 
                                border: "1px solid rgba(244, 67, 54, 0.3)", 
                                borderRadius: "6px",
                                color: "#f44336"
                            }}>
                                <i className="fas fa-exclamation-triangle" style={{ marginRight: "8px" }}></i>
                                {!spaceConfig.spaceName.trim() && (
                                    <p style={{ margin: "0 0 8px 0" }}>Please enter a space name.</p>
                                )}
                                {!Object.values(spaceConfig.selectedFiles).some(Boolean) && (
                                    <p style={{ margin: "0" }}>Please select at least one file to import.</p>
                                )}
                            </div>
                        )}

                        {canProceed() && (
                            <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                <button 
                                    onClick={handleNext}
                                    className="FinalizeImportedWorkspaceDocument__submit-button"
                                    style={{ marginBottom: "60px" }}
                                >
                                    Start Import Process
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Import Progress - Visible when importing */}
            {importProgress && (
                <div className="ImportDocument__main-div">
                    <div className="ImportDocument__content">
                        <h2>Importing Workspace Space</h2>
                        <p className="medium" style={{ marginTop: "20px", marginBottom: "40px" }}>
                            Please wait while we create your workspace space and import the selected files
                        </p>

                        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                            <p><strong>Workspace:</strong> {workspaceDetails?.name || 'Loading...'}</p>
                        </div>

                        <div style={{ 
                            marginTop: "30px", 
                            padding: "30px", 
                            backgroundColor: "rgba(0, 123, 255, 0.1)", 
                            border: "1px solid rgba(0, 123, 255, 0.3)", 
                            borderRadius: "12px",
                            textAlign: "center",
                            maxWidth: "600px",
                            margin: "0 auto"
                        }}>
                            <div style={{ 
                                fontSize: "18px", 
                                fontWeight: "500", 
                                marginBottom: "15px",
                                color: "var(--bb-text)"
                            }}>
                                {importProgress.step}
                            </div>
                            <div style={{ 
                                width: "100%", 
                                height: "10px", 
                                backgroundColor: "rgba(0, 123, 255, 0.2)", 
                                borderRadius: "5px",
                                overflow: "hidden",
                                marginBottom: "15px"
                            }}>
                                <div style={{
                                    width: `${(importProgress.current / importProgress.total) * 100}%`,
                                    height: "100%",
                                    backgroundColor: "#007bff",
                                    transition: "width 0.3s ease"
                                }} />
                            </div>
                            <div style={{ 
                                fontSize: "14px", 
                                color: "var(--bb-text)",
                                opacity: "0.8"
                            }}>
                                {importProgress.current} of {importProgress.total} steps completed
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Back Arrow - Only show when not importing */}
            {!importProgress && (
                <div 
                    className="ImportDocument__arrow ImportDocument__arrow--back" 
                    onClick={handleBack}
                    title="Go back to file upload"
                    aria-label="Go back to file upload"
                >
                    ❮
                </div>
            )}
        </>
    );
}

export default ConfigureWorkspaceSpaceImport;
